import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { getBusinessHealth } from "@/lib/business-health.functions";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { AlertTriangle, ArrowRight, Eye, Info, TrendingDown, TrendingUp } from "lucide-react";

export const Route = createFileRoute("/_authenticated/founder")({
  component: BusinessHealthPage,
  head: () => ({ meta: [{ title: "Business Health · Smartasaurus Admin" }] }),
});

const RANGES = [7, 14, 30, 90];

const TONE: Record<string, string> = {
  critical: "border-red-500/40 bg-red-500/5",
  weak: "border-amber-500/40 bg-amber-500/5",
  ok: "border-sky-500/40 bg-sky-500/5",
  strong: "border-emerald-500/40 bg-emerald-500/5",
};
const DOT: Record<string, string> = {
  critical: "bg-red-500",
  weak: "bg-amber-500",
  ok: "bg-sky-500",
  strong: "bg-emerald-500",
};

const fmt = (n: number) => new Intl.NumberFormat("en-US").format(n ?? 0);

function BusinessHealthPage() {
  const [range, setRange] = useState(7);
  const fn = useServerFn(getBusinessHealth);
  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ["business-health", range],
    queryFn: () => fn({ data: { rangeDays: range } }),
    staleTime: 60_000,
  });

  if (isLoading) return <div className="p-6 text-sm text-muted-foreground">Reading live data…</div>;
  if (error) return <div className="p-6 text-sm text-red-500">Error: {(error as Error).message}</div>;
  if (!data) return null;

  const maxDaily = Math.max(1, ...data.traffic.daily.map((d) => d.sessions));

  return (
    <div className="mx-auto max-w-5xl space-y-8 px-4 py-6 pb-24">
      <header className="flex flex-wrap items-end justify-between gap-3 border-b pb-4">
        <div>
          <h1 className="font-display text-2xl uppercase tracking-tight">Business Health</h1>
          <p className="text-sm text-muted-foreground">
            Every number below is counted directly from the database. Nothing is estimated.
            {data.coverage.daysOfData > 0 && ` ${data.coverage.daysOfData} days of history.`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {RANGES.map((r) => (
            <Button key={r} size="sm" variant={r === range ? "default" : "outline"} onClick={() => setRange(r)}>{r}d</Button>
          ))}
          <Button size="sm" variant="ghost" onClick={() => refetch()} disabled={isFetching}>{isFetching ? "…" : "↻"}</Button>
          <Link to="/admin" search={{ section: "overview" } as never} className="text-xs text-muted-foreground hover:underline">→ Admin</Link>
        </div>
      </header>

      {/* VERDICT */}
      <Card className="p-5">
        <div className="flex flex-wrap items-center gap-5">
          <div className="flex h-20 w-20 shrink-0 flex-col items-center justify-center rounded-full border-4 border-primary/40">
            <span className="font-display text-2xl leading-none">{data.grade}</span>
            <span className="text-[10px] text-muted-foreground">{data.score}/100</span>
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold">{data.stage}</p>
            <p className="mt-1 text-sm text-muted-foreground">{data.verdict}</p>
          </div>
        </div>
      </Card>

      {/* PILLARS */}
      <section className="space-y-3">
        <h2 className="text-lg font-semibold tracking-tight">The five things that decide the business</h2>
        <div className="grid gap-3 md:grid-cols-2">
          {data.pillars.map((p) => (
            <Card key={p.key} className={`p-4 ${TONE[p.status]}`}>
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <span className={`h-2 w-2 rounded-full ${DOT[p.status]}`} />
                  <span className="text-xs font-semibold uppercase tracking-wide">{p.label}</span>
                </div>
                <span className="text-xs text-muted-foreground">{p.metric}</span>
              </div>
              <p className="mt-2 text-sm font-medium">{p.headline}</p>
              <p className="mt-1 text-xs leading-relaxed text-muted-foreground">{p.detail}</p>
            </Card>
          ))}
        </div>
      </section>

      {/* CRITICAL */}
      <section className="space-y-3">
        <h2 className="text-lg font-semibold tracking-tight">What is actually failing</h2>
        {data.criticals.length === 0 ? (
          <Card className="border-emerald-500/40 bg-emerald-500/5 p-4 text-sm">
            Nothing critical. Spend the day on distribution, not on the site.
          </Card>
        ) : (
          <div className="space-y-2">
            {data.criticals.map((c, i) => (
              <Card key={i} className="border-red-500/30 bg-red-500/5 p-4">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-red-500" />
                  <div className="min-w-0">
                    <p className="text-sm font-semibold">{c.title}</p>
                    <p className="mt-1 text-xs text-muted-foreground">{c.why}</p>
                    <p className="mt-2 flex items-start gap-1 text-xs font-medium">
                      <ArrowRight className="mt-0.5 h-3 w-3 shrink-0 text-primary" />{c.action}
                    </p>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </section>

      {/* TRAFFIC */}
      <section className="space-y-3">
        <h2 className="text-lg font-semibold tracking-tight">Traffic</h2>
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          <Metric label="Visits" value={fmt(data.traffic.sessions)} sub={`vs ${fmt(data.traffic.sessionsPrev)} previous ${range}d`} delta={data.traffic.sessionsDelta} />
          <Metric label="Page views" value={fmt(data.traffic.views)} sub={`${data.traffic.pagesPerSession} pages per visit`} />
          <Metric label="Visits / day" value={String(data.traffic.avgSessionsPerDay)} sub={`average over range · ${fmt(data.traffic.crawlerSessions)} bot hits excluded`} />
          <Metric label="Returning" value={data.retention.measurable ? `${data.retention.pct}%` : "-"} sub={data.retention.note} />
        </div>

        <Card className="p-4">
          <div className="mb-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Visits per day</div>
          <div className="flex h-24 items-end gap-1">
            {data.traffic.daily.map((d) => (
              <div key={d.day} className="group relative flex-1" title={`${d.day}: ${d.sessions} visits · ${d.views} views`}>
                <div className="w-full rounded-t bg-primary/70" style={{ height: `${Math.max(3, (d.sessions / maxDaily) * 96)}px` }} />
              </div>
            ))}
          </div>
          <div className="mt-2 flex justify-between text-[10px] text-muted-foreground">
            <span>{data.traffic.daily[0]?.day}</span>
            <span>{data.traffic.daily.at(-1)?.day}</span>
          </div>
        </Card>

        <Card className="p-4">
          <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Where visitors come from</div>
          <div className="space-y-1.5">
            {data.sources.map((s) => (
              <div key={s.bucket}>
                <div className="flex justify-between text-sm"><span>{s.bucket}</span><span className="text-muted-foreground">{s.sessions} · {s.share}%</span></div>
                <div className="mt-1 h-1.5 overflow-hidden rounded bg-muted">
                  <div className="h-full bg-primary" style={{ width: `${s.share}%` }} />
                </div>
              </div>
            ))}
          </div>
        </Card>
      </section>

      {/* JOURNEY */}
      <section className="space-y-3">
        <h2 className="text-lg font-semibold tracking-tight">What visitors actually do</h2>
        <Card className="p-4">
          <div className="space-y-2">
            {data.funnel.map((f, i) => (
              <div key={i}>
                <div className="flex justify-between text-sm">
                  <span>{f.step}</span>
                  <span className="text-muted-foreground">{f.sessions} · {f.pctOfTop}%</span>
                </div>
                <div className="mt-1 h-2 overflow-hidden rounded bg-muted">
                  <div className="h-full bg-orange-500" style={{ width: `${f.pctOfTop}%` }} />
                </div>
              </div>
            ))}
          </div>
          <p className="mt-3 text-[11px] text-muted-foreground">
            Percentages are share of all visits in the range. Reading depth comes from four scroll checkpoints logged per article.
          </p>
        </Card>
      </section>

      {/* BUSINESS SIGNALS */}
      <section className="space-y-3">
        <h2 className="text-lg font-semibold tracking-tight">Business signals</h2>
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          <Metric label="Email subscribers" value={fmt(data.money.subscribers)} sub={`${data.money.subscribersInRange} new in range`} />
          <Metric label="Revenue" value={`€${fmt(data.money.revenueEur)}`} sub={`${data.money.orders} paid orders`} />
          <Metric label="Comments" value={fmt(data.money.comments)} sub="all time, visible" />
          <Metric label="Saves" value={fmt(data.money.saves)} sub="bookmarks in range" />
        </div>
        <Card className="p-4">
          <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Library</div>
          <p className="text-sm">
            {fmt(data.content.published)} published · {data.content.drafts} drafts · {data.content.publishedLast7} published in the last 7 days
            {data.content.lastPublishedAt && ` · last publish ${new Date(data.content.lastPublishedAt).toLocaleDateString()}`}
          </p>
          <p className="mt-1 text-xs text-muted-foreground">
            {fmt(data.content.deadWeight)} published articles ({data.content.deadWeightPct}%) received zero views in this range.
          </p>
          <div className="mt-3 space-y-1">
            {data.content.top.map((t, i) => (
              <div key={t.slug} className="flex justify-between gap-2 text-sm">
                <Link to="/discovery/$slug" params={{ slug: t.slug }} className="truncate hover:underline">{i + 1}. {t.title}</Link>
                <span className="shrink-0 text-muted-foreground">{t.views}</span>
              </div>
            ))}
            {data.content.top.length === 0 && <p className="text-sm text-muted-foreground">No article views recorded in this range.</p>}
          </div>
        </Card>
      </section>

      {/* NEXT */}
      <section className="space-y-3">
        <h2 className="text-lg font-semibold tracking-tight">Where the leverage is</h2>
        <div className="grid gap-3 md:grid-cols-2">
          {data.nextMoves.map((m, i) => (
            <Card key={i} className="p-4">
              <p className="flex items-center gap-2 text-sm font-medium">
                {i === 0 ? <TrendingUp className="h-4 w-4 text-emerald-500" /> : <TrendingDown className="h-4 w-4 text-muted-foreground" />}
                {m.title}
              </p>
              <p className="mt-1 text-xs text-muted-foreground">{m.why}</p>
            </Card>
          ))}
        </div>
      </section>

      {/* HONESTY */}
      <section className="space-y-2">
        <h2 className="flex items-center gap-2 text-lg font-semibold tracking-tight"><Eye className="h-4 w-4" /> What this dashboard cannot tell you</h2>
        <Card className="p-4">
          <ul className="space-y-1.5 text-xs text-muted-foreground">
            {data.blindSpots.map((b, i) => (
              <li key={i} className="flex items-start gap-2"><Info className="mt-0.5 h-3 w-3 shrink-0" />{b}</li>
            ))}
          </ul>
        </Card>
      </section>
    </div>
  );
}

function Metric({ label, value, sub, delta }: { label: string; value: string; sub?: string; delta?: number | null }) {
  return (
    <Card className="p-3">
      <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="mt-1 flex items-baseline gap-2">
        <div className="font-display text-xl">{value}</div>
        {delta !== undefined && delta !== null && (
          <span className={`text-xs ${delta > 0 ? "text-emerald-500" : delta < 0 ? "text-red-500" : "text-muted-foreground"}`}>
            {delta > 0 ? "+" : ""}{delta}%
          </span>
        )}
      </div>
      {sub && <div className="mt-1 text-[11px] leading-snug text-muted-foreground">{sub}</div>}
    </Card>
  );
}
